import React from 'react'
import PayrollCalculator from '../components/PayrollCalculator'
import {MapPin, DollarSign, TrendingUp} from 'lucide-react'

const CaliforniaCalculatorPage = () => {
  return (
    <div>
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-blue-50 to-emerald-50 py-12 md:py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-8">
            <div className="flex items-center justify-center mb-4">
              <MapPin className="w-10 h-10 text-blue-800 mr-3" />
              <h1 className="text-4xl md:text-5xl font-bold text-gray-900">
                California Paycheck Calculator
              </h1>
            </div>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto">
              Calculate your California paycheck with accurate state tax rates. Free payroll calculator for CA employees with SDI calculations.
            </p>
          </div>

          {/* Calculator with CA pre-selected */}
          <PayrollCalculator defaultState="California" />
        </div>
      </section>

      {/* SEO Content */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">
              California Paycheck Calculator - State Tax & SDI Withholding
            </h2>
            <p className="text-gray-600 leading-relaxed mb-4">
              California has one of the most progressive tax systems in the United States, with rates ranging from 1% to 13.3% across ten income brackets. Our California paycheck calculator automatically applies the correct marginal tax rate for your income level. California also has State Disability Insurance (SDI), which is factored into your net pay calculation.
            </p>
            <p className="text-gray-600 leading-relaxed">
              Whether you work in Los Angeles, San Francisco, San Diego, Sacramento, or anywhere in the Golden State, this calculator provides precise estimates of your take home pay after federal taxes, California state taxes, Social Security, Medicare, and SDI deductions.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-8 mb-12">
            <div className="bg-blue-50 p-6 rounded-lg">
              <DollarSign className="w-8 h-8 text-blue-800 mb-3" />
              <h3 className="text-xl font-semibold text-gray-900 mb-3">California Tax Structure</h3>
              <ul className="text-gray-600 space-y-2">
                <li>• 1% - 2% on lower income brackets</li>
                <li>• 4% - 8% on middle income ranges</li>
                <li>• 9.3% - 10.3% on higher incomes</li>
                <li>• 11.3% - 13.3% on top earners</li>
                <li>• SDI: ~1% for disability insurance</li>
              </ul>
            </div>

            <div className="bg-emerald-50 p-6 rounded-lg">
              <TrendingUp className="w-8 h-8 text-emerald-700 mb-3" />
              <h3 className="text-xl font-semibold text-gray-900 mb-3">Why Choose Our Calculator</h3>
              <ul className="text-gray-600 space-y-2">
                <li>• Includes CA SDI calculations</li>
                <li>• Updated with latest tax brackets</li>
                <li>• Accounts for all CA deductions</li>
                <li>• Supports all pay frequencies</li>
              </ul>
            </div>
          </div>

          <div>
            <h2 className="text-3xl font-bold text-gray-900 mb-4">
              How California Paycheck Taxes Work
            </h2>
            <p className="text-gray-600 leading-relaxed">
              California paychecks have more deductions than most states. First, federal income tax is withheld based on IRS guidelines. Then, California state income tax is calculated using the state's ten-bracket progressive system. FICA taxes (Social Security at 6.2% and Medicare at 1.45%) are standard federal deductions. California also requires State Disability Insurance (SDI) at approximately 1% of wages. Our calculator automatically computes all these deductions to show your accurate net pay.
            </p>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="bg-blue-800 py-12">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl font-bold text-white mb-4">
            Calculate Your California Take Home Pay
          </h2>
          <p className="text-blue-100 mb-6">
            Free California paycheck calculator with SDI and accurate state tax rates.
          </p>
          <a
            href="#calculator"
            className="inline-block bg-white text-blue-800 font-semibold px-8 py-3 rounded-lg hover:bg-gray-100 transition-colors"
          >
            Calculate Now
          </a>
        </div>
      </section>
    </div>
  )
}

export default CaliforniaCalculatorPage
